import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, KeyRound, Copy, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const G = "#00604A";
const O = "#F45118";
const ROLES = [["customer", "Customer"], ["assistant", "Mate / Helper"], ["partner", "Partner"], ["homestay_owner", "HomeStay Owner"], ["homestay_manager", "HomeStay Manager"]];
const errText = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);
const waNum = (p) => { const n = String(p || "").replace(/\D/g, ""); return n.length === 10 ? `91${n}` : n; };
const cls = "rounded-xl border-2 px-3 py-2 text-sm w-full";

function resetMessage(link, name) {
  return `Hi ${name || ""}, here is your HeylpMate password reset link (valid 1 hour):\n${link}\n\nIf you didn't ask for this, ignore this message.`;
}

function ResetLinkResult({ res }) {
  const link = `${window.location.origin}/reset-password?token=${res.token}`;
  const wa = `https://wa.me/${waNum(res.phone)}?text=${encodeURIComponent(resetMessage(link, res.name))}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed"));
  return (
    <div data-testid="reset-link-result" className="mt-3 rounded-xl p-3 text-xs" style={{ background: "#F0FDF4" }}>
      <div className="font-bold" style={{ color: G }}>{res.name} · {res.email} {res.role && `· ${res.role}`}</div>
      <div className="mt-1 break-all" data-testid="reset-link-url">{link}</div>
      <div className="mt-2 text-[11px]" style={{ color: "#667085" }}>Expires {new Date(res.expires_at).toLocaleTimeString("en-IN")} · single use</div>
      <div className="mt-2 flex gap-2">
        <button data-testid="reset-link-copy" onClick={copy} className="chip text-xs inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
        <a data-testid="reset-link-wa" href={wa} target="_blank" rel="noreferrer" className="chip text-xs inline-flex items-center gap-1" style={{ color: "#16A34A" }}><MessageCircle className="w-3 h-3" /> Send on WhatsApp</a>
      </div>
    </div>
  );
}

function CreateAccount({ h, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", role: "customer", password: "", property_id: "" });
  const [props, setProps] = useState([]);
  const [last, setLast] = useState(null);
  useEffect(() => { if (f.role === "homestay_manager" && !props.length) axios.get(`${API}/admin/homestay/properties`, h).then((r) => setProps(r.data.items)).catch(() => {}); }, [f.role]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/admin/accounts`, { ...f, property_id: f.property_id || null }, h);
      setLast({ ...r.data, password: f.password, name: f.name, phone: f.phone });
      toast.success("Account created");
      setF({ ...f, name: "", email: "", phone: "", password: "" });
      onCreated();
    } catch (err) { toast.error(errText(err, "Could not create account")); }
  };
  const loginUrl = last && `${window.location.origin}${last.login_path}`;
  const msg = last && `Hi ${last.name}, your HeylpMate login is ready.\nLogin: ${loginUrl}\n${last.partner_code ? `Partner code: ${last.partner_code}` : `Email: ${last.email}`}\nTemporary password: ${last.password}\nPlease change it after first login via "Forgot password?".`;
  return (
    <div data-testid="admin-create-account" className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4E7E5" }}>
      <div className="font-bold text-sm inline-flex items-center gap-1.5" style={{ color: G }}><UserPlus className="w-4 h-4" /> Create a login</div>
      <form onSubmit={submit} className="mt-3 grid grid-cols-2 gap-2">
        <input required placeholder="Full name" data-testid="acc-name-input" className={cls} value={f.name} onChange={set("name")} />
        <input required placeholder="Phone" data-testid="acc-phone-input" className={cls} value={f.phone} onChange={set("phone")} />
        <input required type="email" placeholder="Email" data-testid="acc-email-input" className={cls} value={f.email} onChange={set("email")} />
        <input required minLength={6} placeholder="Temporary password" data-testid="acc-password-input" className={cls} value={f.password} onChange={set("password")} />
        <select data-testid="acc-role-select" className={cls} value={f.role} onChange={set("role")}>
          {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {f.role === "homestay_manager" ? (
          <select required data-testid="acc-property-select" className={cls} value={f.property_id} onChange={set("property_id")}>
            <option value="">Select property…</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.city}</option>)}
          </select>
        ) : <div />}
        <button data-testid="acc-create-submit" className="col-span-2 rounded-full py-2 text-sm font-bold text-white" style={{ background: G }}>Create account</button>
      </form>
      {last && (
        <div data-testid="acc-created-result" className="mt-3 rounded-xl p-3 text-xs" style={{ background: "#FFF3EA" }}>
          <div className="font-bold" style={{ color: O }}>Created {last.role}{last.partner_code ? ` · code ${last.partner_code}` : ""}</div>
          <pre className="mt-1 whitespace-pre-wrap font-sans">{msg}</pre>
          <a data-testid="acc-created-wa" href={`https://wa.me/${waNum(last.phone)}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" className="chip text-xs mt-2 inline-flex items-center gap-1" style={{ color: "#16A34A" }}><MessageCircle className="w-3 h-3" /> Send login on WhatsApp</a>
        </div>
      )}
    </div>
  );
}

function ResetTool({ h, requests, onDone }) {
  const [email, setEmail] = useState("");
  const [res, setRes] = useState(null);
  const gen = async (em) => {
    try { const r = await axios.post(`${API}/admin/accounts/reset-link`, { email: em }, h); setRes(r.data); onDone(); }
    catch (err) { toast.error(errText(err, "Could not create reset link")); }
  };
  return (
    <div data-testid="admin-reset-tool" className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4E7E5" }}>
      <div className="font-bold text-sm inline-flex items-center gap-1.5" style={{ color: G }}><KeyRound className="w-4 h-4" /> Password reset link</div>
      <form onSubmit={(e) => { e.preventDefault(); gen(email); }} className="mt-3 flex gap-2">
        <input required type="email" placeholder="User / partner email" data-testid="reset-email-input" className={cls} value={email} onChange={(e) => setEmail(e.target.value)} />
        <button data-testid="reset-generate-btn" className="rounded-full px-4 text-sm font-bold text-white shrink-0" style={{ background: O }}>Generate</button>
      </form>
      {res && <ResetLinkResult res={res} />}
      <div className="mt-4 text-xs font-bold uppercase tracking-widest" style={{ color: "#667085" }}>"Forgot password" requests ({requests.length})</div>
      {requests.length === 0 && <div className="text-xs mt-1" style={{ color: "#667085" }}>No pending requests.</div>}
      {requests.map((q) => (
        <div key={q.id} data-testid={`reset-request-${q.id}`} className="flex items-center justify-between py-2 border-t text-sm" style={{ borderColor: "#E4E7E5" }}>
          <span><b>{q.name}</b> <span className="text-xs" style={{ color: "#667085" }}>{q.email} · {q.phone || "no phone"}</span></span>
          <button data-testid={`reset-request-gen-${q.id}`} onClick={() => gen(q.email)} className="chip text-xs" style={{ color: O }}>Generate link</button>
        </div>
      ))}
    </div>
  );
}

export function AdminAccounts({ token }) {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [recent, setRecent] = useState([]);
  const [requests, setRequests] = useState([]);
  const load = useCallback(() => {
    const hh = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/admin/accounts/recent`, hh).then((r) => setRecent(r.data.items)).catch(() => {});
    axios.get(`${API}/admin/reset-requests`, hh).then((r) => setRequests(r.data.items)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-accounts" className="space-y-3">
      <div className="grid lg:grid-cols-2 gap-3">
        <CreateAccount h={h} onCreated={load} />
        <ResetTool h={h} requests={requests} onDone={load} />
      </div>
      <div data-testid="admin-recent-accounts" className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4E7E5" }}>
        <div className="font-bold text-sm" style={{ color: G }}>Created by admin ({recent.length})</div>
        {recent.map((u) => (
          <div key={`${u.email}-${u.role}`} className="flex justify-between py-1.5 border-t text-xs" style={{ borderColor: "#E4E7E5" }}>
            <span><b>{u.name}</b> · {u.email}{u.partner_code ? ` · ${u.partner_code}` : ""}</span>
            <span className="chip text-[10px]">{u.role}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
